import React from "react";

export default function TermsAndConditions() {
  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl p-8 md:p-12 my-10 text-gray-800 leading-relaxed">
      <h1 className="text-3xl font-bold text-[#000000] mb-6">
        Primehost Web Design Terms and Conditions
      </h1>

      <p className="text-sm text-gray-500 mb-8">
        Effective Date: 18 October 2025
      </p>

      {/* PART A: GENERAL TERMS AND CONDITIONS */}
      <h2 className="text-2xl font-semibold text-[#000000] mb-4">
        PART A: GENERAL TERMS AND CONDITIONS
      </h2>

      <section className="space-y-4">
        <h3 className="font-semibold text-lg text-[#000000]">
          1. Introduction and Acceptance
        </h3>
        <p>
          By submitting the web design specification form provided by Primehost or engaging our web design services, you acknowledge and agree to these Terms and Conditions. This document governs all web design services provided by Primehost and forms a binding agreement between you (the Client) and Primehost.
        </p>
        <p>
          These Terms and Conditions are supplemental to the{" "}
          <a
            href="https://www.primehost.co.za/#/terms"
            className="text-[#FF5841] underline"
          >
            Primehost Terms of Service
          </a>
          . In the event of any inconsistency between these terms and the main Terms of Service, the main Terms of Service shall prevail.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          2. Purpose and Scope
        </h3>
        <p>
          The web design specification form is designed to systematically gather all relevant information required to scope, plan, and deliver your web design project. This includes details about your business, design and branding preferences, content specifications, target audience analysis, technical requirements, and project expectations.
        </p>
        <p>
          Submission of the web design specification form serves as the initial stage of engagement and does not create a binding contract for delivery of services. Primehost will review the submitted information and may contact you for further consultation, after which a formal quotation or proposal will be presented where applicable.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          3. Data Collection and Privacy
        </h3>
        <p>
          All information you provide via the web design specification form will be processed in accordance with our Privacy Policy and South African law. The data collected includes, but is not limited to, business details, contact information, branding assets, visual preferences, written content, audience information, and technical specifications.
        </p>
        <p>
          Your data will only be used for the purposes of evaluating, planning, and fulfilling the web design services requested. Primehost implements suitable organisational and technical measures to safeguard your information as required by law. Personal and business information collected may be retained in accordance with legal and regulatory obligations as well as our data retention policies.
        </p>
        <p>
          For further details on how your data will be managed, refer to the Primehost Privacy Policy available on our website.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          4. Client Responsibilities and Information Requirements
        </h3>
        <p>
          You are responsible for ensuring that all information submitted is accurate, complete, and up to date. Omissions, inaccuracies, or delays in providing requested information may impact project timelines and deliverables.
        </p>
        <p>
          You agree to engage openly and promptly with the Primehost team regarding form clarifications, additional requirements, or project queries. Project work will not commence until all necessary information has been provided and any requested documentation, files, or assets have been supplied.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          5. Content Provision and Timeline
        </h3>
        <p>
          Clients must provide all required content within 14 calendar days of project commencement. Failure to provide content within this timeframe may result in project cancellation or delays. Project timeline will be extended if content delivery is delayed beyond the Client&apos;s control.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          6. Content Ownership, Rights and Standards
        </h3>
        <p>
          Information and materials you provide must be your own, or you must have explicit permission to use them. You warrant and guarantee that you own all intellectual property rights to provided content including text, images, logos, videos, and other materials.
        </p>
        <p>
          You indemnify Primehost against any copyright infringement claims or claims arising from intellectual property infringement or unauthorised use of third-party materials provided by you.
        </p>
        <p>
          Primehost may provide generic stock images and placeholder content where Client content is unavailable. You grant Primehost licence to use provided content for website creation and hosting purposes.
        </p>
        <p>
          You are solely responsible for ensuring all content complies with applicable local and international laws. Prohibited content includes adult content, illegal activities, hate speech, discriminatory content, content violating platform policies, malicious software, or content infringing third party rights.
        </p>
        <p>
          Primehost reserves the right to refuse or remove any content deemed inappropriate or non-compliant.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          7. File and Content Specifications
        </h3>
        <p>
          Maximum file upload size is 10MB per individual file. Supported formats include PNG, SVG, AI, EPS, JPG and PDF. Total website storage is subject to hosting package limitations where applicable.
        </p>
        <p>
          Clients are responsible for providing web optimised, compressed images. Primehost may provide generic content on request.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          8. Technical Specifications
        </h3>
        <p>
          All websites will include standard responsive design for mobile and desktop compatibility. Websites will include contact forms, image galleries, basic SEO optimisation, and social media integration links where applicable.
        </p>
        <p>
          Excluded features include custom coding beyond standard specifications, shopping carts, payment gateways, booking systems, databases, membership areas, forums, or advanced interactive features unless specifically quoted and agreed upon.
        </p>
        <p>
          All websites will display "Website by Primehost" footer credit with link to{" "}
          <a
            href="https://www.primehost.co.za/"
            className="text-[#FF5841] underline"
          >
            https://www.primehost.co.za/
          </a>
          .
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          9. Hosting Terms
        </h3>
        <p>
          Website hosting will be provided at Primehost&apos;s discretion on servers located in the US, Europe, or other international locations. Specific hosting country requests will incur additional charges and require separate quotation.
        </p>
        <p>
          Hosting renewal rates are subject to Primehost&apos;s current pricing at time of renewal. Domain name registration and renewal costs are excluded and charged separately unless specifically included in a package or agreement.
        </p>
        <p>
          Late payment of hosting renewals may result in website and email service suspension after a 7 day grace period. Suspended services may incur reactivation fees.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          10. Support and Maintenance
        </h3>
        <p>
          Basic technical support is included during Primehost business hours (Monday to Friday, 8:00 AM to 4:00 PM SAST). Support covers hosting related issues, basic functionality problems, and minor technical assistance.
        </p>
        <p>
          Excluded support includes content creation, major design changes, third party integrations, and custom development beyond the scope of the original project.
        </p>
        <p>
          Security updates and system maintenance are included as part of hosting service where applicable. Regular automated backups are included; however, data recovery services may incur additional fees. Performance optimisation beyond standard hosting specifications is not guaranteed.
        </p>
        <p>
          Standard hosting uptime target is 99.5% excluding scheduled maintenance. Response time for support queries is 24 to 48 hours during business days. Emergency support is available for critical issues affecting website accessibility.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          11. Limitation of Liability
        </h3>
        <p>
          Primehost is not liable for any direct or indirect losses arising from provision of incorrect or incomplete information in the form, delays in supplying required details, or circumstances outside Primehost&apos;s reasonable control.
        </p>
        <p>
          Primehost shall not be liable for any business losses, lost profits, lost revenue, or consequential damages arising from website downtime, performance issues, or service interruptions.
        </p>
        <p>
          Maximum liability is strictly limited to the total amount paid by Client for services in the preceding 12 months. Primehost is not responsible for search engine rankings, website traffic, conversion rates, or business performance metrics.
        </p>
        <p>
          Clients assume full responsibility for website content accuracy, legal compliance, and appropriateness. Clients must promptly notify Primehost of any technical issues or security concerns.
        </p>
        <p>
          Primehost is not liable for delays or failures caused by circumstances beyond reasonable control including natural disasters, government actions, internet infrastructure failures, or third party service provider issues.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          12. Portfolio and Marketing Rights
        </h3>
        <p>
          Primehost reserves the right to use completed websites as portfolio examples in marketing materials, case studies, and promotional content. Client information may be used as testimonials and references unless Client specifically opts out in writing.
        </p>
        <p>
          Screenshots and website examples may be displayed on Primehost&apos;s website and marketing materials.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          13. Governing Law and Jurisdiction
        </h3>
        <p>
          These terms and conditions are governed by the laws of South Africa. Any disputes will be subject to the jurisdiction of South African courts.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          14. Modifications to Terms
        </h3>
        <p>
          Primehost reserves the right to modify these terms at any time. Clients will be notified of significant changes via email or account notifications.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          15. Severability
        </h3>
        <p>
          If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          16. Contact Information
        </h3>
        <p>
          For questions regarding these terms and conditions, please contact:
        </p>
        <ul className="list-disc ml-8">
          <li>
            <span className="font-semibold">Email:</span>{" "}
            <a
              href="mailto:support@primehost.co.za"
              className="text-[#FF5841] underline"
            >
              support@primehost.co.za
            </a>
          </li>
          <li>
            <span className="font-semibold">Website:</span>{" "}
            <a
              href="https://primehost.co.za"
              className="text-[#FF5841] underline"
            >
              https://primehost.co.za
            </a>
          </li>
        </ul>
      </section>

      {/* PART B: STANDARD WEB DESIGN SERVICES */}
      <h2 className="text-2xl font-semibold text-[#000000] mt-10 mb-4">
        PART B: STANDARD WEB DESIGN SERVICES
      </h2>

      <section className="space-y-4">
        <h3 className="font-semibold text-lg text-[#000000]">
          17. Application and Scope
        </h3>
        <p>
          This section applies to Clients engaging Primehost for standard paid web design services. Standard services encompass bespoke web design projects tailored to Client specifications and requirements.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          18. Project Commencement and Quotations
        </h3>
        <p>
          Project work will commence only upon mutual agreement, completion of any prerequisite documentation, and acceptance of the final scope and commercial terms as outlined in the formal quotation or proposal.
        </p>
        <p>
          Primehost will review submitted information and provide a formal quotation or proposal outlining project scope, deliverables, timelines, and costs. The acceptance of a completed specification form and subsequent project commencement is subject to confirmation from Primehost and compliance with all contractual and payment obligations.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          19. Payment Terms for Standard Services
        </h3>
        <p>
          Full payment for web design services must be received and cleared before design work commences, unless alternative payment arrangements have been agreed upon in writing.
        </p>
        <p>
          All payments are non-refundable once design work has commenced. Payment terms, schedules, and methods will be specified in the project quotation or proposal.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          20. Design Timeline for Standard Services
        </h3>
        <p>
          Website design turnaround time will be specified in the project proposal and is calculated from the date of content approval, payment clearance, and project commencement.
        </p>
        <p>
          Timeline may be extended due to Client delays, content issues, or technical complications beyond Primehost&apos;s control. Rush delivery requests may incur additional charges.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          21. Revision Policy for Standard Services
        </h3>
        <p>
          Initial Design Phase: Five revisions are included during the initial design process.
          <br />
          Additional Revisions: Beyond the included five revisions will incur additional charges at Primehost&apos;s current hourly rates.
          <br />
          Annual Maintenance: Clients are entitled to two minor revisions per calendar year at no additional cost.
        </p>
        <p>
          <span className="font-semibold">Revision Classifications:</span>
          <ul className="list-disc ml-8">
            <li>Minor Revisions: Text changes up to 200 words, contact information updates, address changes, phone number updates, email address changes, minor image replacements.</li>
            <li>Major Revisions: Layout changes, additional pages, new functionality, significant content restructuring, design overhauls, template changes.</li>
          </ul>
          Primehost has sole discretion in determining whether a revision constitutes minor or major work.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          22. Intellectual Property for Standard Services
        </h3>
        <p>
          Upon full payment for web design services, all intellectual property rights including website designs, code, design elements, and related materials are transferred to and become the property of the Client.
        </p>
        <p>
          Client receives full ownership and unrestricted rights to use, modify, transfer, or redistribute the completed website and all associated materials once payment has been made in full.
        </p>
        <p>
          Primehost retains no ownership rights or claims to the completed website design once full payment has been received.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          23. Website Migration for Standard Services
        </h3>
        <p>
          Clients who have paid in full for web design services retain full rights to migrate their website to any hosting provider of their choice. Primehost will provide reasonable assistance with migration where requested, subject to availability and any applicable support fees.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          24. Termination for Standard Services
        </h3>
        <p>
          Either party may terminate the agreement with 30 days written notice. Upon termination, Client retains all rights to completed work for which full payment has been made.
        </p>
        <p>
          If termination occurs before project completion, Client will be charged for work completed to date based on the percentage of project completion and any costs already incurred.
        </p>
      </section>

      {/* PART C: COMPLIMENTARY WEBSITE DESIGN CAMPAIGN */}
      <h2 className="text-2xl font-semibold text-[#000000] mt-10 mb-4">
        PART C: COMPLIMENTARY WEBSITE DESIGN CAMPAIGN
      </h2>

      <section className="space-y-4">
        <h3 className="font-semibold text-lg text-[#000000]">
          25. Campaign Overview and Eligibility
        </h3>
        <p>
          Primehost offers complimentary website design services to qualifying clients during promotional campaign periods. Campaign availability, duration, and terms are subject to change and are offered at Primehost&apos;s sole discretion.
        </p>
        <p>
          Primehost is not obliged to honour campaign terms after the closing date of the campaign or after a certain number of spots have been taken. Primehost has the right to change the campaign closing dates and the number of available spots at any time without prior notice.
        </p>
        <p>
          This campaign offer includes the design of 1 to 5 webpage static websites for existing clients based on their current hosting package tier, and new clients who commit to a minimum 12 month web and email hosting agreement.
        </p>
        <p>
          <span className="font-semibold">Eligibility Requirements:</span>
        </p>
        <ul className="list-disc ml-8">
          <li>
            Existing Clients: Must have an active hosting account in good standing with Primehost. Number of website page allocation depends on current hosting package tier.
          </li>
          <li>
            New Clients: Must sign up for hosting services for a minimum 12 month period with full upfront payment.
          </li>
          <li>
            Limit of one website per client, business entity, or legal person.
          </li>
          <li>
            Offer is non transferable and cannot be assigned to third parties.
          </li>
        </ul>
        <span className="font-semibold">Exclusions:</span>
        <ul className="list-disc ml-8">
          <li>
            This offer cannot be combined with any other promotions, discounts, or special offers.
          </li>
          <li>
            Clients who have previously received free website design services from Primehost are excluded.
          </li>
          <li>
            Reseller accounts and wholesale clients do not qualify and are excluded from this campaign.
          </li>
        </ul>

        <h3 className="font-semibold text-lg text-[#000000]">
          26. Campaign Validity and Modifications
        </h3>
        <p>
          Campaign offers are valid during specified promotional periods only or until withdrawn by Primehost. All applications must be submitted and approved within the campaign period.
        </p>
        <p>
          Primehost reserves the absolute right to modify, suspend, or terminate any campaign at any time without prior notice.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          27. Technical Specifications for Campaign Websites
        </h3>
        <p>
          All campaign websites will be static websites built using Primehost&apos;s standard templates and content management platforms. Website allocation ranges from 1 to 5 pages depending on hosting package or agreement.
        </p>
        <p>
          Excluded features include e-commerce functionality and any features listed as excluded in Part A, Section 8.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          28. Payment Requirements for Campaign Clients
        </h3>
        <p>
          New Clients: Full 12 month web and email hosting payment must be received and cleared before design work commences.
        </p>
        <p>
          Existing Clients: Must maintain current hosting account in good standing throughout design process.
        </p>
        <p>
          All payments are non-refundable once design work has commenced. Campaign clients do not pay separately for web design services; the complimentary design is contingent upon maintaining active hosting services with Primehost.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          29. Hosting Obligations for Campaign Clients
        </h3>
        <p>
          Website must remain hosted with Primehost or its designated partners for the duration of the hosting agreement. Website cannot be migrated to external hosting providers unless the Client pays a transfer fee.
        </p>
        <p>
          The transfer fee is equivalent to the fixed fee applicable to design similar websites at the time the transfer request is made, as determined by Primehost&apos;s current pricing structure.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          30. Design Timeline for Campaign Clients
        </h3>
        <p>
          Website design turnaround time is 30 business days from date of content approval and project commencement. Timeline may be extended due to Client delays, content issues, or technical complications beyond Primehost&apos;s control.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          31. Revision Policy for Campaign Clients
        </h3>
        <p>
          Initial Design Phase: Two revisions are included during the initial design process.
          <br />
          Additional Revisions: Beyond the included two revisions will incur additional charges at Primehost&apos;s current hourly rates.
          <br />
          Annual Maintenance: Campaign clients are entitled to two minor revisions per calendar year at no additional cost.
        </p>
        <p>
          Revision classifications are as defined in Part B, Section 21. Primehost has sole discretion in determining whether a revision constitutes minor or major work.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          32. Intellectual Property for Campaign Clients
        </h3>
        <p>
          All website designs, templates, code, and design elements created under the campaign remain the exclusive intellectual property of Primehost.
        </p>
        <p>
          Campaign clients receive a non exclusive, non transferable right to use the website design solely while hosting services remain active with Primehost. This right of use permits the Client to display and operate the website for business purposes but does not confer ownership of the underlying code, templates, or design elements.
        </p>
        <p>
          Campaign clients cannot claim ownership of design elements, templates, or proprietary code used in website construction. Clients cannot reverse engineer, modify, or attempt to extract proprietary code or design elements.
        </p>
        <p>
          Removal of "Website by Primehost" branding requires separate agreement and may incur additional fees. Clients cannot sublicence or redistribute any design elements to third parties.
        </p>

        <h3 className="font-semibold text-lg text-[#000000]">
          33. Termination for Campaign Clients
        </h3>
        <p>
          Either party may terminate the hosting agreement with 30 days written notice after completion of the initial 12 month commitment period.
        </p>
        <p>
          Primehost may terminate services immediately for policy violations, non payment, illegal activities, or breach of terms.
        </p>
        <p>
          <span className="font-semibold">Early Termination Consequences:</span>
        </p>
        <ul className="list-disc ml-8">
          <li>
            Early termination by Client forfeits all rights to the website design and any prepaid hosting fees.
          </li>
          <li>
            Website will be permanently removed from servers upon termination.
          </li>
          <li>
            No refunds will be provided for unused hosting periods or design services.
          </li>
        </ul>
        <span className="font-semibold">Data and Content Recovery:</span>
        <ul className="list-disc ml-8">
          <li>
            Upon termination, Client has 30 days to request content backup other than the website and the related code. Fees may apply for backup services.
          </li>
          <li>
            After 30 days, all data and content may be permanently deleted.
          </li>
          <li>
            Client is responsible for migrating any recoverable content to alternative hosting. The website code and design may not be migrated unless the transfer fee specified in Section 29 has been paid.
          </li>
        </ul>
      </section>

      <h2 className="text-2xl font-semibold text-[#000000] mt-10 mb-4">
        FINAL PROVISIONS
      </h2>
      <p>
        By accepting this offer and proceeding with website design services, Client acknowledges they have read, understood, and agree to be bound by these terms and conditions in their entirety.
      </p>
      <p className="mt-6 text-sm text-gray-500">Last Updated: 18 October 2025</p>
    </div>
  );
}